/**
 * The Anthropic API backend — per-token, metered, isolated from the subscription's rate limits.
 *
 * ⛔ NO SAMPLING PARAMETERS ARE EVER SENT. `temperature`, `top_p` and `top_k` return a 400 on the
 * current model family, so `temperature: 0` breaks every call instead of pinning it. `budget_tokens`
 * is likewise gone: thinking is `{type:"adaptive"}` and the effort tier lives in `output_config`.
 * Determinism does not come from sampling on these models — it comes from caching the output.
 * There is a structural guard below rather than a comment, because this exact mistake has been made
 * in a written plan before.
 *
 * ⛔ CACHE LAYOUT IS THE IMPLEMENTATION, not a tuning detail (spec.md §7.4, design research §7.3):
 *
 *     system:   [A] corpus   ttl 1h   ← breakpoint 1
 *               [B] rubric   ttl 1h   ← breakpoint 2
 *     messages: [C] diff     ttl 5m   ← breakpoint 3
 *               [D] the per-dimension instruction — LAST, no breakpoint
 *
 * ⚠️ Mixed TTLs require every 1h block to precede every 5m block. The order above is load-bearing,
 * not stylistic, and there are at most four breakpoints in total.
 *
 * ⚠️ Below the minimum cacheable prefix — 512 tokens on Opus 5, 1,024 on Sonnet 5 — nothing caches
 * and NO ERROR IS RETURNED. That case is recorded as `not_cached`, never as a miss; see cache.ts.
 */

import type { AnthropicApiConfig } from "../config/schema.js"
import { cacheStats } from "./cache.js"
import {
  BackendAuthError,
  BackendError,
  ProbeFailedError,
  type Backend,
  type BackendRequest,
  type BackendResult,
  type StopReason,
} from "./types.js"

/**
 * ⛔ Every one of these returns a 400 on the current models. The guard runs on the assembled body
 * immediately before the request, so a key added anywhere — including by a future `extra_body` knob
 * — fails here rather than as an opaque 400 from the server.
 */
export const FORBIDDEN_BODY_KEYS: readonly string[] = ["temperature", "top_p", "top_k", "budget_tokens"]

/**
 * JSON Schema keywords the structured-output grammar does not accept.
 *
 * ⛔ MEASURED, NOT GUESSED. A live request carrying `{"type":"integer","minimum":1}` — a line number,
 * which cannot sensibly be zero — was rejected outright:
 *   `output_config.format.schema: For 'integer' type, property 'minimum' is not supported`
 * The whole review returned nothing because one numeric bound was expressed in the schema.
 *
 * ⚠️ REMOVING A CONSTRAINT FROM THE GRAMMAR DOES NOT REMOVE IT FROM THE PROGRAM. Every one of these
 * is re-checked after parsing — `parseCandidates` in src/engine/run.ts rejects a line that is not a
 * positive safe integer — so the bound still holds; it is simply enforced where it can be. Dropping
 * a constraint silently and enforcing it nowhere would be the actual bug.
 */
export const UNSUPPORTED_SCHEMA_KEYWORDS: readonly string[] = [
  "minimum",
  "maximum",
  "exclusiveMinimum",
  "exclusiveMaximum",
  "multipleOf",
  "minLength",
  "maxLength",
  "pattern",
  "minItems",
  "maxItems",
  "uniqueItems",
  // ⚠️ `format` is NOT in this list. String formats — date-time, email, uri, uuid — ARE supported by
  // the structured-output grammar, and stripping one would silently widen the schema. Only the
  // numeric and length constraints below are rejected.
]

/** Recursively drop the keywords above. Returns a copy; the caller's schema is never mutated. */
export function sanitiseSchema(node: unknown): unknown {
  if (Array.isArray(node)) return node.map(sanitiseSchema)
  if (node === null || typeof node !== "object") return node
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
    // ⚠️ `properties` is a map of FIELD NAMES, so a field legitimately called "pattern" or "format"
    // must survive. Only keyword positions are filtered, never the contents of `properties`.
    if (k === "properties" && v !== null && typeof v === "object") {
      const props: Record<string, unknown> = {}
      for (const [name, sub] of Object.entries(v as Record<string, unknown>)) props[name] = sanitiseSchema(sub)
      out[k] = props
      continue
    }
    if (UNSUPPORTED_SCHEMA_KEYWORDS.includes(k)) continue
    out[k] = sanitiseSchema(v)
  }
  return out
}

export function assertNoSamplingParameters(body: Readonly<Record<string, unknown>>): void {
  const walk = (node: unknown, path: string): void => {
    if (node === null || typeof node !== "object") return
    if (Array.isArray(node)) {
      node.forEach((v, i) => walk(v, `${path}[${i}]`))
      return
    }
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      if (FORBIDDEN_BODY_KEYS.includes(k)) {
        throw new BackendError(
          `${path}${path === "" ? "" : "."}${k} would 400 on this model family — the current models ` +
            `reject sampling parameters, and determinism comes from caching, never from sampling`,
          "anthropic_api",
        )
      }
      walk(v, path === "" ? k : `${path}.${k}`)
    }
  }
  walk(body, "")
}

type TextBlock = { type: "text"; text: string; cache_control?: { type: "ephemeral"; ttl: "5m" | "1h" } }

const ephemeral = (ttl: "5m" | "1h"): { type: "ephemeral"; ttl: "5m" | "1h" } => ({ type: "ephemeral", ttl })

/**
 * Assemble the request body.
 *
 * ⭐ Exported so the suite can assert on the bytes we would have sent without a network call. "No
 * sampling parameter is ever sent" is only a real claim if something checks the actual body.
 */
export function buildBody(cfg: AnthropicApiConfig, req: BackendRequest): Record<string, unknown> {
  const system: TextBlock[] = []
  // ⚠️ An empty text block is rejected by the API, so an absent corpus contributes no block at all
  // rather than an empty one — and correspondingly spends no breakpoint.
  if (req.systemPrompt.corpus !== "") {
    system.push({ type: "text", text: req.systemPrompt.corpus, ...cacheIf(cfg, "1h") })
  }
  if (req.systemPrompt.rubric !== "") {
    system.push({ type: "text", text: req.systemPrompt.rubric, ...cacheIf(cfg, "1h") })
  }

  const content: TextBlock[] = [
    // [C] the diff — shared by every dimension of one pull request, so it is worth a 5m breakpoint.
    { type: "text", text: req.diff, ...cacheIf(cfg, "5m") },
    // [D] ⛔ LAST and deliberately uncached. It is the only part that varies per dimension; cached,
    // it would restart the prefix on every call and the run would never see a read.
    { type: "text", text: req.dimensionInstruction },
  ]

  const body: Record<string, unknown> = {
    model: req.model ?? cfg.model,
    max_tokens: cfg.maxTokens,
    system,
    messages: [{ role: "user", content }],
    // ⛔ `{type:"adaptive"}` — there is no budget_tokens on this family and a knob for it would be a
    // knob wired to a 400.
    // ⛔ `display` LIVES ON `thinking`, NOT ON `output_config`. Measured 2026-09-15 against the live
    // API: `output_config.thinking_display` is rejected with
    //   `output_config.thinking_display: Extra inputs are not permitted`
    // The valid keys of `output_config` are `effort`, `format` and `task_budget`; everything about
    // how thinking is surfaced belongs to the `thinking` object.
    thinking:
      cfg.thinking === "adaptive"
        ? { type: "adaptive", display: cfg.thinkingDisplay }
        : { type: "disabled" },
    output_config: {
      effort: req.effort ?? cfg.effort,
      // Grammar-constrained, not prompt-coerced. Survives thinking, unlike a forced tool call.
      format: { type: "json_schema", schema: sanitiseSchema(req.outputSchema) },
    },
  }
  // ⚠️ Required above roughly 16k max_tokens or the request times out before the response lands.
  if (cfg.stream) body["stream"] = true

  assertNoSamplingParameters(body)
  return body
}

function cacheIf(cfg: AnthropicApiConfig, ttl: "5m" | "1h"): { cache_control?: { type: "ephemeral"; ttl: "5m" | "1h" } } {
  if (!cfg.promptCache) return {}
  // The configured TTL caps the long blocks; the diff is always short-lived.
  const effective = ttl === "1h" ? cfg.promptCacheTtl : "5m"
  return { cache_control: ephemeral(effective) }
}

/** Retried per config.md: transient status codes and connection errors, nothing else. */
const RETRYABLE_STATUS = new Set([408, 409, 429, 500, 502, 503, 504, 529])

export interface AnthropicApiOptions {
  readonly config: AnthropicApiConfig
  /**
   * ⛔ The credential VALUE, resolved by the caller from `config.apiKeyName`. It travels in a header
   * and is never placed in a URL, an argv, or an error message.
   */
  readonly apiKey: string
  readonly fetchImpl?: typeof fetch
  readonly now?: () => number
  /** Injected so a retry test does not actually sleep. */
  readonly sleep?: (ms: number) => Promise<void>
}

interface ApiUsage {
  input_tokens?: number
  output_tokens?: number
  cache_creation_input_tokens?: number
  cache_read_input_tokens?: number
  cache_creation?: { ephemeral_1h_input_tokens?: number; ephemeral_5m_input_tokens?: number }
}

export class AnthropicApiBackend implements Backend {
  readonly kind = "anthropic_api" as const
  private readonly cfg: AnthropicApiConfig
  private readonly apiKey: string
  private readonly f: typeof fetch
  private readonly now: () => number
  private readonly sleep: (ms: number) => Promise<void>

  constructor(opts: AnthropicApiOptions) {
    this.cfg = opts.config
    this.apiKey = opts.apiKey
    this.f = opts.fetchImpl ?? fetch
    this.now = opts.now ?? Date.now
    this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)))
  }

  private url(): string {
    const base = this.cfg.baseUrl === "" ? "https://api.anthropic.com" : this.cfg.baseUrl.replace(/\/+$/, "")
    return `${base}/v1/messages`
  }

  async run(req: BackendRequest): Promise<BackendResult> {
    const body = buildBody(this.cfg, req)
    const started = this.now()

    let lastError: BackendError | undefined
    for (let attempt = 0; attempt <= this.cfg.maxRetries; attempt++) {
      let res: Response
      try {
        res = await this.f(this.url(), {
          method: "POST",
          headers: {
            // ⛔ Header, never a query parameter: a URL reaches logs, proxies and error messages.
            "x-api-key": this.apiKey,
            "anthropic-version": "2023-06-01",
            "content-type": "application/json",
            "user-agent": "sibyl",
          },
          body: JSON.stringify(body),
        })
      } catch (e) {
        // ⛔ A connection error means the call did not RUN. It says nothing about the credential.
        lastError = new ProbeFailedError(
          `the Anthropic API could not be reached: ${(e as Error).message}`,
          "anthropic_api",
        )
        if (attempt < this.cfg.maxRetries) {
          await this.sleep(backoffMs(attempt))
          continue
        }
        throw lastError
      }

      if (res.status === 401 || res.status === 403) {
        // ⛔ Reached the server and was rejected — this one IS a credential verdict, unlike the above.
        throw new BackendAuthError(
          `the Anthropic API rejected the credential named by ${this.cfg.apiKeyName} (HTTP ${res.status})`,
          "anthropic_api",
          (await safeText(res)).slice(0, 400),
        )
      }
      if (!res.ok) {
        // ⛔ THE API'S OWN SENTENCE GOES IN THE MESSAGE. This line used to read `Anthropic API 400`
        // and put the reason in `evidence`, where nothing printed it: a live run reported that
        // sentence five times and the actual cause — "output_config.format.schema: For 'integer'
        // type, property 'minimum' is not supported" — was never shown. A status code names a
        // category; it never names a fix. Identical defect to the one fixed in claude-cli.ts.
        const detail = (await safeText(res)).slice(0, 2000)
        let reason = ""
        try {
          reason = String((JSON.parse(detail) as { error?: { message?: unknown } }).error?.message ?? "")
        } catch {
          reason = detail.slice(0, 300)
        }
        lastError = new BackendError(
          reason === "" ? `Anthropic API ${res.status} with no explanation` : `Anthropic API ${res.status}: ${reason}`,
          "anthropic_api",
          detail,
        )
        if (RETRYABLE_STATUS.has(res.status) && attempt < this.cfg.maxRetries) {
          await this.sleep(backoffMs(attempt))
          continue
        }
        throw lastError
      }

      const parsed = this.cfg.stream ? await readStream(res) : await readSingle(res)
      return this.toResult(req, parsed, this.now() - started)
    }
    /* c8 ignore next */
    throw lastError ?? new BackendError("the retry loop ended without a result", "anthropic_api")
  }

  private toResult(req: BackendRequest, m: ParsedMessage, durationMs: number): BackendResult {
    const u = m.usage
    const created = u.cache_creation_input_tokens ?? 0
    const read = u.cache_read_input_tokens ?? 0

    // ⚠️ A refusal is HTTP 200 with `stop_reason: "refusal"` and output that may not match the
    // schema. There is no server-side retry, so it is surfaced rather than parsed hopefully.
    const stopReason = normaliseStopReason(m.stopReason)
    if (stopReason === "refusal") {
      throw new BackendError(
        `the model refused this dimension (${req.dimensionId}); the response carries no schema-valid findings`,
        "anthropic_api",
        m.text.slice(0, 400),
      )
    }

    return {
      backend: "anthropic_api",
      dimensionId: req.dimensionId,
      model: req.model ?? this.cfg.model,
      effort: req.effort ?? this.cfg.effort,
      // ⚠️ `max_tokens` means the JSON is truncated and will not parse. The parse failure is
      // reported with the stop reason attached, so the cause is not guessed at.
      output: parseOutput(m.text, stopReason),
      raw: m.text,
      stopReason,
      usage: { inputTokens: u.input_tokens ?? 0, outputTokens: u.output_tokens ?? 0 },
      cache: cacheStats({
        createdTokens: created,
        readTokens: read,
        uncachedInputTokens: u.input_tokens ?? 0,
        created1hTokens: u.cache_creation?.ephemeral_1h_input_tokens ?? 0,
        created5mTokens: u.cache_creation?.ephemeral_5m_input_tokens ?? 0,
      }),
      durationMs,
      permissionDenials: [],
    }
  }
}

/** 0.5s, 1s, 2s … Bounded by `max_retries`; wall clock can reach timeout × (retries + 1). */
function backoffMs(attempt: number): number {
  return 500 * 2 ** attempt
}

interface ParsedMessage {
  readonly text: string
  readonly stopReason: unknown
  readonly usage: ApiUsage
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text()
  } catch {
    return ""
  }
}

async function readSingle(res: Response): Promise<ParsedMessage> {
  const d = (await res.json()) as {
    content?: Array<{ type?: string; text?: string }>
    stop_reason?: unknown
    usage?: ApiUsage
  }
  const text = (d.content ?? [])
    .filter((b) => b.type === "text")
    .map((b) => b.text ?? "")
    .join("")
  return { text, stopReason: d.stop_reason, usage: d.usage ?? {} }
}

/**
 * Read a server-sent-event stream into one message.
 *
 * ⚠️ Usage arrives in two places and BOTH are needed: `message_start` carries the input and cache
 * counts, `message_delta` carries the final output count. Taking only one of them produces a cache
 * ledger that is silently half empty — the exact failure this project is reacting to.
 *
 * ⛔ FRAMES SPLIT ACROSS CHUNKS. A network chunk boundary falls wherever TCP puts it, not at an
 * event boundary, so an incomplete trailing line is HELD and joined to the next chunk. Parsing each
 * chunk independently drops whichever event straddled the split — and `message_start` is the first
 * event, so in practice what it drops is the entire input and cache accounting. The run then looks
 * successful with a cache ledger reading zero. Caught by a test that splits mid-frame on purpose.
 */
export async function readStream(res: Response): Promise<ParsedMessage> {
  let text = ""
  let stopReason: unknown
  let usage: ApiUsage = {}
  let pending = ""

  const handle = (line: string): void => {
    if (!line.startsWith("data:")) return
    const payload = line.slice(5).trim()
    if (payload === "" || payload === "[DONE]") return
    let ev: Record<string, unknown>
    try {
      ev = JSON.parse(payload) as Record<string, unknown>
    } catch {
      return // not a frame we can read; never a silent substitution of a guess
    }
    switch (ev["type"]) {
      case "message_start": {
        const msg = ev["message"] as { usage?: ApiUsage } | undefined
        usage = { ...usage, ...(msg?.usage ?? {}) }
        return
      }
      case "content_block_delta": {
        const delta = ev["delta"] as { type?: string; text?: string } | undefined
        if (delta?.type === "text_delta" && typeof delta.text === "string") text += delta.text
        return
      }
      case "message_delta": {
        const delta = ev["delta"] as { stop_reason?: unknown } | undefined
        if (delta?.stop_reason !== undefined) stopReason = delta.stop_reason
        const u = ev["usage"] as ApiUsage | undefined
        if (u !== undefined) usage = { ...usage, ...u }
        return
      }
      case "error": {
        const err = ev["error"] as { message?: string } | undefined
        throw new BackendError(
          `the Anthropic stream carried an error: ${err?.message ?? "unknown"}`,
          "anthropic_api",
        )
      }
      default:
        return
    }
  }

  for await (const chunk of iterateBody(res)) {
    const lines = (pending + chunk).split("\n")
    // The last element is whatever came after the final newline: possibly half a frame.
    pending = lines.pop() ?? ""
    for (const line of lines) handle(line)
  }
  // ⚠️ A stream that does not end with a newline leaves its last event in `pending`. Dropping it
  // loses `message_delta`, which is the only place the final output-token count appears.
  handle(pending)
  return { text, stopReason, usage }
}

/**
 * ⚠️ A `Response.body` is a web ReadableStream. Node's implementation is async-iterable and the
 * WHATWG one is not, so both shapes are handled — otherwise this works on Node and throws
 * "not iterable" under any other fetch implementation someone injects.
 */
async function* iterateBody(res: Response): AsyncGenerator<string> {
  const body = res.body as unknown
  if (body === null || body === undefined) {
    yield await safeText(res)
    return
  }
  const decoder = new TextDecoder()
  const asIterable = body as AsyncIterable<Uint8Array | string>
  if (typeof asIterable[Symbol.asyncIterator] === "function") {
    for await (const chunk of asIterable) {
      yield typeof chunk === "string" ? chunk : decoder.decode(chunk, { stream: true })
    }
    return
  }
  const reader = (body as ReadableStream<Uint8Array>).getReader()
  for (;;) {
    const { done, value } = await reader.read()
    if (done) return
    if (value !== undefined) yield decoder.decode(value, { stream: true })
  }
}

function parseOutput(text: string, stopReason: StopReason): unknown {
  try {
    return JSON.parse(text)
  } catch {
    if (stopReason === "max_tokens") {
      throw new BackendError(
        "the response was truncated at max_tokens, so its structured output is a partial object — " +
          "raise [backend.anthropic_api].max_tokens or chunk the diff further",
        "anthropic_api",
        text.slice(-400),
      )
    }
    throw new BackendError("the response was not parseable JSON", "anthropic_api", text.slice(0, 400))
  }
}

/** ⛔ A runtime floor on an otherwise exhaustive mapping. See claude-cli.ts for why. */
function normaliseStopReason(raw: unknown): StopReason {
  switch (raw) {
    case "end_turn":
    case "max_tokens":
    case "refusal":
    case "stop_sequence":
    case "tool_use":
      return raw
    default:
      return "other"
  }
}
